
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useInterval } from "@/hooks/use-interval";
import { useCarouselImages } from "@/hooks/useCarouselImages";

// Fallback images in case database is empty
const fallbackImages = [
  {
    url: "https://images.unsplash.com/photo-1600618528240-fb9fc964b853?q=60&w=1200&auto=format&fit=crop",
    alt: "Serene wellness space for holistic therapy"
  },
  {
    url: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=60&w=1200&auto=format&fit=crop",
    alt: "Holistic therapy session environment"
  },
  {
    url: "https://images.unsplash.com/photo-1593811167562-9cef47bfc4d7?q=60&w=1200&auto=format&fit=crop",
    alt: "Professional wellness consultation"
  },
  {
    url: "https://images.unsplash.com/photo-1529693662653-9d480530a697?q=60&w=1200&auto=format&fit=crop",
    alt: "Peaceful nature setting for therapeutic relaxation"
  }
];

const HeroSection = () => {
  const { user, activeRole } = useAuth();
  const { t } = useTranslation('homepage');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { images: carouselImages, loading } = useCarouselImages();

  // Use carousel images from database or fallback to hardcoded ones
  const images = carouselImages.length > 0 
    ? carouselImages.map(img => ({ url: img.file_path, alt: img.alt_text }))
    : fallbackImages;

  // Auto advance carousel every 8 seconds
  useInterval(() => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
  }, 8000);

  return (
    <section className="relative min-h-[520px] md:min-h-[600px] lg:min-h-[680px] overflow-hidden">
      {/* Background carousel images */}
      <div className="absolute inset-0">
        {!loading && images.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={image.url}
              alt={image.alt}
              className="w-full h-full object-cover"
              loading={index === 0 ? "eager" : "lazy"}
              crossOrigin="anonymous"
              sizes="100vw"
              fetchPriority={index === 0 ? "high" : "low"}
            />
          </div>
        ))}
        {/* Warm overlay */}
        <div className="absolute inset-0 bg-warm-50/80 backdrop-blur-[2px]" />
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 relative z-20 pt-28 pb-20 md:pt-36 md:pb-28 flex flex-col items-center text-center">
        <h1 className="text-4xl md:text-5xl lg:text-6xl leading-tight text-warm-900 font-brand max-w-4xl">
          {t('heroTitle')}
        </h1>
        <p className="mt-6 text-lg md:text-xl text-warm-700 leading-relaxed max-w-2xl">
          {t('heroSubtitle')}
        </p>
        <div className="flex flex-col sm:flex-row gap-5 mt-10">
          <Button asChild size="lg" className="bg-gradient-brand text-white hover:opacity-90 rounded-full min-w-[240px] h-14 px-10 text-lg font-semibold shadow-xl">
            <Link to="/booking-choice">
              {t('bookAppointment', { ns: 'navigation' })} <ArrowRight size={18} className="ml-2" />
            </Link>
          </Button>
          {user ? (
            <Button asChild size="lg" variant="outline" className="border-2 border-brand-500 text-brand-700 hover:bg-brand-50 bg-white/60 rounded-full min-w-[240px] h-14 px-10 text-lg font-semibold shadow-lg">
              <Link to={activeRole === 'client' ? '/client-dashboard' : '/practitioner-dashboard'}>
                {t('myDashboard', { ns: 'navigation' })}
              </Link>
            </Button>
          ) : (
            <Button asChild size="lg" variant="outline" className="border-2 border-brand-500 text-brand-700 hover:bg-brand-50 bg-white/60 rounded-full min-w-[240px] h-14 px-10 text-lg font-semibold shadow-lg">
              <Link to="/become-practitioner">
                {t('joinAsPractitioner')}
              </Link>
            </Button>
          )}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
